// addAppEstimate.js — THE COST BEFORE A BUILD, where the builder writes
// (workspace presets programme, WS lane `feat/build-estimate-ui`; the server
// half is lane CORE-EST, S1-API.md §10 "CORE-EST — build estimate").
//
// Every persona of the build walks said the same: no price shows before a
// build, and a failed try costs credits ("776 credits bought no working
// board"). The server answers what a builder turn's FIRST model step will
// reserve and whether this workspace can pay it, before the turn is sent —
// no model call, no write:
//
//   POST /core/workspaces/:workspaceId/build-estimate     (sdk.estimateBuild)
//   body    the builder turn's own body: content, context (surface
//           'add-app-builder', uiLanguage, …), modelMode, targetAgentId, plus
//           conversationId when the thread exists
//   answer  { surface, step: { full, min }, build: null,
//             basis: 'first-step-only', balance, waived, waiver,
//             allocationExceeded, canStart, enough }
//
// THE LINE (pages/add-app.js ComposerWrap.BuildEstimate — the /add-app
// builder and the home's Build tab: one page in two places). While the person
// types, one quiet line under the composer says what the first step costs:
//   waived              "Your first build is free."          (only then)
//   priced              "This first step costs about {count} credits."
//   not enough /        the price, the reason, and the way out — an admin's
//   allocation          link to the credit page, a member's "ask a workspace
//                       admin" (the credits lane's own refusal words,
//                       functions/creditsRefusal.js)
// `step.full` is the FIRST STEP, never the price of the build: `build` is null
// (the ledger cannot tell one builder turn's steps apart yet).
//
// THE ASK: debounced while the person types, and again on a thread, workspace,
// language or agent change (the composer's `stateDeps`). One request in
// flight; the newest ask wins and an older answer is dropped. The draft rides
// the POST body only: never a URL, never a log line, never root state (the
// record below holds numbers, flags and ids).
//
// NEVER A BROKEN STATE: no SDK method (`sdk.estimateBuild` arrives with the
// SDK release), the null client, a refused place or any error → no line, no
// console output. A place the server refused stays quiet, so a failure never
// turns into a request loop: for good on 400 / 401 / 403 / 404, one minute on
// anything else (a 503 NO_AI_PROVIDERS, a lost connection).
//
// NOT PRICED (the server's own gaps, S1-API.md §10.5): an app's own thread
// (`_addAppProjectId` — the project-scoped builder conversation), and a turn
// that is already running (its credits move while it runs).
//
// FRANK: every export is self-contained (FA204); siblings through `this.call`.
// `this` is the calling element. The bookkeeping (the timer, the request in
// flight, the refused places) lives on the root as a plain `__` property:
// never reactive, never written by `root.update`.

// The place an estimate prices — the workspace the turn is billed to and the
// thread it runs in: 'ws|thread', 'ws|new' before the thread exists, or ''
// where nothing is priced (signed out, no workspace, an app's own thread).
// Every key is read up front (reactive callers).
export function addAppEstimatePlace(root) {
  const el = this
  const r = root && typeof root === 'object' ? root : null
  if (!r || !el || typeof el.call !== 'function') return ''
  const signedIn = !!r.signedIn
  const bound = !!r._addAppProjectId
  const active = r._ax_activeSessionId ? String(r._ax_activeSessionId) : ''
  let ws = ''
  try {
    ws = String(el.call('activeWorkspaceId', r) || '')
  } catch (_) {
    ws = ''
  }
  if (!signedIn || bound || !ws) return ''
  // a local 's-…' id names no server thread yet: the turn makes one
  return ws + '|' + (active && !active.startsWith('s-') ? active : 'new')
}

// Can an estimate show HERE — a place to price and the SDK method to ask
// with? The feature gate is the method itself (the shell's init-await proxy
// reads a method the SDK lacks as undefined), never a version guess; the null
// client answers every method, so it is refused by name. The line reserves its
// box only while this is true (pages/add-app.js BuildEstimate).
export function addAppEstimateReady(root) {
  const el = this
  if (!el || typeof el.call !== 'function') return false
  // Home keeps the builder mounted while another view is shown. Its draft
  // survives, but pricing pauses until Create is visible again. Dedicated
  // and pane builders have no HomeBuildView ancestor and remain independent.
  if (
    typeof el.lookup === 'function' &&
    el.lookup('HomeBuildView') &&
    !el.call('homeBuildShown', root)
  ) {
    return false
  }
  if (!el.call('addAppEstimatePlace', root)) return false
  let sdk = null
  try {
    sdk = el.call('getSDK')
  } catch (_) {
    sdk = null
  }
  return !!sdk && sdk.isNullClient !== true && typeof sdk.estimateBuild === 'function'
}

// The builder turn's own body, for the estimate → { workspaceId, key, body } or
// null when there is nothing to price. The fields are the transport's for the
// build surface (functions/simoneAiTransport.js `dispatchContext`, scope
// 'addapp'); `content`, `modelMode` and `targetAgentId` are the ones the SDK
// adds to the turn (sdk.ai `_streamWorkspaceTurn`). Not sent: an attached
// image (it would ride every pause in the typing; it adds a few credits to the
// first step) and a picked page element (the build surface carries none).
export function addAppEstimateBody(root, draft) {
  const el = this
  const r = root && typeof root === 'object' ? root : null
  const text = typeof draft === 'string' ? draft.trim() : ''
  if (!r || !text || !el || typeof el.call !== 'function') return null
  const key = String(el.call('addAppEstimatePlace', r) || '')
  const at = key.indexOf('|')
  if (at <= 0) return null
  const workspaceId = key.slice(0, at)
  const thread = key.slice(at + 1)
  // the model mode the SDK sends with the turn (sdk.ai.getModelMode)
  let modelMode = ''
  try {
    const ai = el.call('getAiService')
    if (ai && typeof ai.getModelMode === 'function') modelMode = String(ai.getModelMode() || '')
  } catch (_) {
    modelMode = ''
  }
  if (!modelMode) modelMode = typeof r.modelMode === 'string' && r.modelMode ? r.modelMode : 'auto'
  const context = {
    surface: 'add-app-builder',
    extensionProjectId: null,
    pickedElements: null,
    targetAgentId: r._targetAgentId || null,
    targetAgentName: r._targetAgentName || null
  }
  if (typeof r.lang === 'string' && r.lang) context.uiLanguage = r.lang
  const body = { content: text, modelMode, targetAgentId: r._targetAgentId || null, context }
  if (thread && thread !== 'new') body.conversationId = thread
  return { workspaceId, key, body }
}

// The server's answer → the record the line reads (numbers, flags and the
// place; never the waiver's ids, never the text), or null for an answer that
// is not an estimate's shape — a line is never drawn from a guess.
export function addAppEstimateRecord(data, key) {
  const count = (v) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null)
  if (!data || typeof data !== 'object' || !key) return null
  const step = data.step && typeof data.step === 'object' ? data.step : null
  const full = count(step && step.full)
  if (full === null) return null
  for (const flag of ['waived', 'canStart', 'enough']) {
    if (typeof data[flag] !== 'boolean') return null
  }
  return {
    key: String(key),
    at: Date.now(),
    full,
    min: count(step.min),
    waived: data.waived,
    allocationExceeded: data.allocationExceeded === true,
    canStart: data.canStart,
    enough: data.enough
  }
}

// ASK — the composer calls this on every draft, thread, workspace, language or
// agent change (pages/add-app.js PromptTextarea `stateDeps`). It waits for a
// pause in the typing, then sends (addAppEstimateSend). Nothing to price, no
// SDK method, a running turn or a refused place: the line goes, nothing is
// sent. → true when an ask is waiting.
export function addAppEstimateAsk(root, draft) {
  const PAUSE_MS = 700
  const el = this
  const r = root && typeof root === 'object' ? root : (el && el.state && el.state.root) || null
  if (!r || !el || typeof el.call !== 'function') return false
  if (!r.__addAppEstimateRun || typeof r.__addAppEstimateRun !== 'object') {
    r.__addAppEstimateRun = { seq: 0, timer: null, flight: null, queued: null, refused: {} }
  }
  const run = r.__addAppEstimateRun
  if (run.timer) clearTimeout(run.timer)
  run.timer = null
  const quiet = () => {
    run.seq += 1
    run.queued = null
    if (r._addAppEstimate && typeof r.update === 'function') {
      r.update({ _addAppEstimate: null }, { preventFetch: true })
    }
    return false
  }
  // a turn in flight: its credits move — the composer asks again after it
  if (r._ax_chatTyping) return quiet()
  if (!el.call('addAppEstimateReady', r)) return quiet()
  const ask = el.call('addAppEstimateBody', r, draft)
  if (!ask) return quiet()
  const now = Date.now()
  const refusedAll = Number(run.refused['*']) || 0
  const refusedHere = Number(run.refused[ask.key]) || 0
  if (now < refusedAll || now < refusedHere) return quiet()
  // the price on screen belongs to another place (a thread, a workspace): it
  // goes now; the same place keeps it until the new answer lands (no flicker)
  if (r._addAppEstimate && r._addAppEstimate.key !== ask.key && typeof r.update === 'function') {
    r.update({ _addAppEstimate: null }, { preventFetch: true })
  }
  run.seq += 1
  const seq = run.seq
  run.timer = setTimeout(() => {
    run.timer = null
    el.call('addAppEstimateSend', r, seq, ask)
  }, PAUSE_MS)
  return true
}

// SEND — one request in flight. An ask made while one flies waits for it and
// goes after it, if it is still the newest; an answer that is no longer the
// newest ask's is dropped. → Promise<boolean> (a record written).
export function addAppEstimateSend(root, seq, ask) {
  const RETRY_MS = 60 * 1000
  const el = this
  const r = root && typeof root === 'object' ? root : null
  const run = r && r.__addAppEstimateRun
  if (!run || !ask || seq !== run.seq || !el || typeof el.call !== 'function') {
    return Promise.resolve(false)
  }
  if (run.flight) {
    run.queued = { seq, ask }
    return Promise.resolve(false)
  }
  let sdk = null
  try {
    sdk = el.call('getSDK')
  } catch (_) {
    sdk = null
  }
  if (!sdk || sdk.isNullClient === true || typeof sdk.estimateBuild !== 'function') {
    return Promise.resolve(false)
  }
  const flight = { seq }
  run.flight = flight
  let request
  try {
    request = Promise.resolve(sdk.estimateBuild(ask.workspaceId, ask.body))
  } catch (err) {
    request = Promise.reject(err)
  }
  const after = () => {
    if (run.flight === flight) run.flight = null
    const next = run.queued
    run.queued = null
    if (next && next.seq === run.seq) el.call('addAppEstimateSend', r, next.seq, next.ask)
  }
  return request.then(
    (data) => {
      let wrote = false
      if (seq === run.seq && el.call('addAppEstimatePlace', r) === ask.key) {
        const record = el.call('addAppEstimateRecord', data, ask.key)
        if (typeof r.update === 'function') {
          r.update({ _addAppEstimate: record || null }, { preventFetch: true })
        }
        wrote = !!record
      }
      after()
      return wrote
    },
    (err) => {
      const status = Number(err && err.status) || 0
      // the session or the contract: no place prices — stays quiet
      if (status === 401 || status === 400) run.refused['*'] = Infinity
      // the member gate, a thread the turn would refuse, a route not there yet
      else if (status === 403 || status === 404) run.refused[ask.key] = Infinity
      // no AI provider, a lost connection, a server error: a minute's rest
      else run.refused[ask.key] = Date.now() + RETRY_MS
      if (seq === run.seq && r._addAppEstimate && typeof r.update === 'function') {
        r.update({ _addAppEstimate: null }, { preventFetch: true })
      }
      after()
      return false
    }
  )
}

// STOP — the composer unmounts: no ask waits, an answer in flight is dropped,
// the line goes. A retained home builder becoming hidden runs Ask's quiet
// path through its home-visibility state dependency instead.
export function addAppEstimateStop(root) {
  const el = this
  const r = root && typeof root === 'object' ? root : (el && el.state && el.state.root) || null
  if (!r) return false
  const run = r.__addAppEstimateRun
  if (run && typeof run === 'object') {
    if (run.timer) clearTimeout(run.timer)
    run.timer = null
    run.queued = null
    run.seq = (Number(run.seq) || 0) + 1
  }
  if (r._addAppEstimate && typeof r.update === 'function') {
    r.update({ _addAppEstimate: null }, { preventFetch: true })
  }
  return true
}

// THE LINE — the record of THIS place, in the session language, for this
// viewer → { kind, lead, reason, way, words, link, after } or null (no line):
//   kind    'free' | 'price' | 'short' | 'allocation'
//   lead    the first sentence: free, or the first step's price
//   reason  what stands in the way ('' when nothing does)
//   way     null, or the way out: an admin's `{ before, link, after }` (the
//           link opens the credit page), a member's `{ text }`
//   words / link / after — the same sentences as the line draws them: the
//           text up to the link, the link's words ('' for a member), the text
//           after it (the template's own spaces kept at both seams)
// Numbers read in the person's locale (the session language through Intl).
export function addAppEstimateLine(root) {
  const el = this
  const r = root && typeof root === 'object' ? root : (el && el.state && el.state.root) || null
  if (!r || !el || typeof el.call !== 'function') return null
  // every key read up front (reactive callers)
  const held = r._addAppEstimate
  const typing = !!r._ax_chatTyping
  const switching = !!r._addAppSwitching
  const place = el.call('addAppEstimatePlace', r)
  if (!held || typeof held !== 'object' || typing || switching) return null
  if (!place || place !== held.key) return null
  const t = (key) => {
    const v = el.call('polyglot', key)
    return v == null ? '' : String(v)
  }
  const view = (kind, lead, reason, way) => {
    const said = [lead, reason, way && way.text].filter(Boolean).join(' ')
    if (!way || !way.link) return { kind, lead, reason, way, words: said, link: '', after: '' }
    return {
      kind,
      lead,
      reason,
      way,
      words: (said ? said + ' ' : '') + (way.before || ''),
      link: way.link,
      after: way.after || ''
    }
  }
  if (held.waived === true) return view('free', t('workspace.addApp.estimate.free'), '', null)
  let lang = ''
  try {
    lang = String(el.call('getActiveLang') || '')
  } catch (_) {
    lang = ''
  }
  if (!lang) lang = typeof r.lang === 'string' && r.lang ? r.lang : 'en'
  const credits = Math.ceil(Number(held.full) || 0)
  let count = String(credits)
  try {
    count = new Intl.NumberFormat(lang, { maximumFractionDigits: 0 }).format(credits)
  } catch (_) {
    count = String(credits)
  }
  const lead = t('workspace.addApp.estimate.step').replace('{count}', () => count)
  const allocation = held.allocationExceeded === true
  if (!allocation && held.canStart === true && held.enough === true) {
    return view('price', lead, '', null)
  }
  let admin = false
  try {
    admin = !!el.call('currentUserIsAdmin', r)
  } catch (_) {
    admin = false
  }
  // the credits lane's way out, by role: the admin's words ARE the link
  // ("{link} to keep going." — the template keeps each language's order)
  const linked = (template, label) => {
    const at = template.indexOf('{link}')
    if (at < 0) return { before: '', link: label, after: '' }
    return { before: template.slice(0, at), link: label, after: template.slice(at + 6) }
  }
  if (allocation) {
    return view(
      'allocation',
      lead,
      t('workspace.credits.chat.allocation'),
      admin
        ? linked(t('workspace.credits.chat.limitAdmin'), t('workspace.credits.chat.limitLink'))
        : { text: t('workspace.credits.chat.limitMember') }
    )
  }
  return view(
    'short',
    lead,
    t('workspace.credits.chat.notEnough'),
    admin
      ? linked(t('workspace.credits.chat.balanceAdmin'), t('workspace.credits.chat.addLink'))
      : { text: t('workspace.credits.chat.balanceMember') }
  )
}
